import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, Modal, message, DatePicker, Input, InputNumber, Select, Dropdown } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import voucherApi from "../../../api/voucherApi";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faCheck, faFileImport, faChartBar, faCopy, faChevronDown, faGift, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { DATE_PRESETS } from "../../../utils/date-presets";
import VoucherSummaryBar from "./voucher-summary-bar";
import MonthlyTaxReportModal from "../../../components/monthly-tax-report-modal";

// Nhãn cho báo cáo thuế phần đổi voucher (tiền = số dư bị trừ, không phải mệnh giá)
const VOUCHER_TAX_LABELS = {
    users: "Số user đổi",
    count: "Số lượt đổi",
    totalAmount: "Tổng tiền trừ số dư (đ)",
    totalValue: "Tổng mệnh giá (đ)",
    zeroCount: "Lượt 0đ / tặng",
    detailTooltip: "Mỗi lượt đổi 1 dòng: user, voucher, mệnh giá, tiền trừ số dư (không kèm mã voucher)",
};

function VoucherList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: parseInt(searchParams.get("page") || "1", 10),
        pageSize: parseInt(searchParams.get("limit") || "20", 10),
        total: 0,
    });
    const [filters, setFilters] = useState({
        status: searchParams.get("status") || "",
        partner: searchParams.get("partner") || "",
        value: searchParams.get("value") || "",
        code: searchParams.get("code") || "",
        start_date: searchParams.get("start_date") || "",
        end_date: searchParams.get("end_date") || "",
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkModalVisible, setBulkModalVisible] = useState(false);
    const [bulkRedeemCost, setBulkRedeemCost] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    // Bulk assign voucher state
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [assignUserIds, setAssignUserIds] = useState("");
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignResult, setAssignResult] = useState(null); // null = form view, object = result view
    const [voucherStats, setVoucherStats] = useState({});
    const [taxReportOpen, setTaxReportOpen] = useState(false);

    // Sync state to URL — replace so back button returns to previous page, not previous filter state
    useEffect(() => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.partner) params.partner = filters.partner;
        if (filters.value) params.value = filters.value;
        if (filters.code) params.code = filters.code;
        if (filters.start_date) params.start_date = filters.start_date;
        if (filters.end_date) params.end_date = filters.end_date;
        if (pagination.current > 1) params.page = String(pagination.current);
        if (pagination.pageSize !== 20) params.limit = String(pagination.pageSize);
        setSearchParams(params, { replace: true });
    }, [filters, pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Reset to page 1 when filters change
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [filters.status, filters.partner, filters.value, filters.code, filters.start_date, filters.end_date]);

    useEffect(() => {
        fetchVouchers();
    }, [pagination.current, pagination.pageSize, filters]);

    const fetchVouchers = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                ...filters,
            };
            // Auto-sort by most recently taken when filtering TAKEN status
            if (filters.status === "TAKEN" && !params.sort) {
                params.sort = "-taken_at";
            }
            // Map date filter to correct API fields (created_at[gte]/[lte])
            if (params.start_date) {
                params["created_at[gte]"] = Math.floor(new Date(params.start_date).getTime() / 1000);
                delete params.start_date;
            }
            if (params.end_date) {
                const endDate = new Date(params.end_date);
                endDate.setHours(23, 59, 59, 999);
                params["created_at[lte]"] = Math.floor(endDate.getTime() / 1000);
                delete params.end_date;
            }
            Object.keys(params).forEach((key) => {
                if (params[key] === "" || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });
            const { data } = await voucherApi.getAllVouchers(params);
            setVouchers(data.data.vouchers || []);
            setPagination((prev) => ({
                ...prev,
                total: data.data.totalVouchers || 0,
            }));
            if (data.data.stats) setVoucherStats(data.data.stats);
        } catch (error) {
            console.error(error);
            message.error("Không thể tải danh sách voucher");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            await voucherApi.approveVoucher(id);
            message.success("Duyệt voucher thành công");
            fetchVouchers();
        } catch (error) {
            message.error("Không thể duyệt voucher");
        }
    };

    const handleBulkUpdateRedeemCost = async () => {
        if (bulkRedeemCost === null || bulkRedeemCost === undefined || bulkRedeemCost === "" || Number(bulkRedeemCost) < 0) {
            message.error("Vui lòng nhập giá đổi hợp lệ (≥ 0, 0 = miễn phí)");
            return;
        }
        try {
            setBulkLoading(true);
            await voucherApi.bulkUpdateVouchers({
                voucherIds: selectedRowKeys,
                updateData: { redeem_cost: bulkRedeemCost },
            });
            message.success(`Đã cập nhật giá đổi cho ${selectedRowKeys.length} voucher`);
            setBulkModalVisible(false);
            setBulkRedeemCost(null);
            setSelectedRowKeys([]);
            fetchVouchers();
        } catch (error) {
            message.error(error.response?.data?.message || "Không thể cập nhật giá đổi");
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkAssign = async () => {
        const userIds = assignUserIds
            .split(/[,\n;]+/)
            .map((s) => s.trim())
            .filter(Boolean)
            .map(Number)
            .filter((id) => id > 0 && Number.isInteger(id));

        if (userIds.length === 0) {
            message.error("Vui lòng nhập ít nhất 1 User ID hợp lệ");
            return;
        }

        // Only use ACTIVE vouchers from selection
        const activeVoucherIds = selectedRowKeys.filter((id) => {
            const v = vouchers.find((voucher) => voucher.id === id);
            return v && v.status === "ACTIVE";
        });

        if (activeVoucherIds.length === 0) {
            message.error("Không có voucher ACTIVE nào được chọn");
            return;
        }

        if (activeVoucherIds.length < userIds.length) {
            message.warning(`Chỉ có ${activeVoucherIds.length} voucher ACTIVE cho ${userIds.length} users`);
        }

        try {
            setAssignLoading(true);
            const { data } = await voucherApi.bulkAssignVouchers({
                userIds,
                voucherIds: activeVoucherIds,
                note: "Admin tặng voucher",
            });
            setAssignResult(data.data);
            setSelectedRowKeys([]);
            fetchVouchers();
        } catch (error) {
            message.error(error.response?.data?.message || "Không thể gán voucher");
        } finally {
            setAssignLoading(false);
        }
    };

    const handleCopy = (record) => {
        // Copy voucher data and navigate to create page with pre-filled data
        const copiedData = {
            code: `${record.code}_COPY_${Date.now()}`,
            secret_code: record.secret_code || "",
            title: `${record.title} (Copy)`,
            description: record.description || "",
            value: record.value,
            redeem_cost: record.redeem_cost,
            partner: record.partner,
            image_url: record.image_url || "",
            target_url: record.target_url || "",
            start_at: record.start_at ? dayjs.unix(record.start_at) : null,
            end_at: record.end_at ? dayjs.unix(record.end_at) : null,
        };
        navigate("/voucher/create", { state: { copiedData } });
    };

    const doStatusChange = async (voucherId, newStatus) => {
        try {
            // Optimistic update - update UI immediately
            setVouchers((prevVouchers) =>
                prevVouchers.map((voucher) =>
                    voucher.id === voucherId ? { ...voucher, status: newStatus } : voucher
                )
            );
            
            await voucherApi.updateVoucher(voucherId, { status: newStatus });
            message.success("Thay đổi trạng thái thành công");
            
            // Fetch fresh data from server to ensure consistency
            await fetchVouchers();
        } catch (error) {
            // Revert optimistic update on error
            fetchVouchers();
            const errorMsg = error.response?.data?.message || "Không thể thay đổi trạng thái";
            message.error(errorMsg);
        }
    };

    const handleStatusChange = (voucherId, newStatus, record) => {
        // Đưa voucher TAKEN (đã có người lấy) về ACTIVE => thu hồi lượt đổi (hoàn điểm + xoá người đã lấy)
        if (newStatus === "ACTIVE" && (record?.status === "TAKEN" || record?.taken_by_user_id)) {
            const takenName = record.takenByUser?.name || (record.taken_by_user_id ? `user #${record.taken_by_user_id}` : "user");
            Modal.confirm({
                title: "Thu hồi lượt đã đổi?",
                content: `Voucher đang được "${takenName}" lấy. Chuyển về ACTIVE sẽ hoàn lại điểm đã trừ, xoá người đã lấy để người khác đổi lại được.`,
                okText: "Thu hồi & kích hoạt",
                cancelText: "Huỷ",
                okButtonProps: { danger: true },
                onOk: () => doStatusChange(voucherId, newStatus),
            });
            return;
        }
        doStatusChange(voucherId, newStatus);
    };

    const formatDate = (timestamp) => {
        return format(new Date(timestamp * 1000), "dd/MM/yyyy HH:mm");
    };

    const statusColors = {
        PENDING: "orange",
        ACTIVE: "green",
        TAKEN: "blue",
        OUT_OF_STOCK: "red",
        EXPIRED: "gray",
    };

    const partnerNames = {
        SHOPEE: "Shopee",
        LAZADA: "Lazada",
        TIKTOK: "TikTok",
        OTHER: "Khác",
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 60,
        },
        {
            title: "Ngày thêm",
            dataIndex: "created_at",
            key: "created_at",
            width: 140,
            render: (ts) => ts ? formatDate(ts) : "-",
        },
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            width: 150,
        },
        {
            title: "Tiêu đề",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
        },
        {
            title: "Đối tác",
            dataIndex: "partner",
            key: "partner",
            width: 100,
            render: (partner) => partnerNames[partner] || partner,
        },
        {
            title: "Mệnh giá",
            dataIndex: "value",
            key: "value",
            width: 120,
            render: (value) => `${value.toLocaleString("vi-VN")}đ`,
        },
        {
            title: "Giá đổi",
            dataIndex: "redeem_cost",
            key: "redeem_cost",
            width: 120,
            render: (cost) => `${cost.toLocaleString("vi-VN")} điểm`,
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            width: 180,
            render: (status, record) => {
                const statusOptions = [
                    { value: "PENDING", label: "PENDING", color: statusColors.PENDING },
                    { value: "ACTIVE", label: "ACTIVE", color: statusColors.ACTIVE },
                    { value: "TAKEN", label: "TAKEN", color: statusColors.TAKEN },
                    { value: "OUT_OF_STOCK", label: "OUT_OF_STOCK", color: statusColors.OUT_OF_STOCK },
                    { value: "EXPIRED", label: "EXPIRED", color: statusColors.EXPIRED },
                ];

                const menuItems = statusOptions
                    .filter((option) => option.value !== status)
                    .map((option) => ({
                        key: option.value,
                        label: <Tag color={option.color}>{option.label}</Tag>,
                        onClick: () => handleStatusChange(record.id, option.value, record),
                    }));

                return (
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={["click"]}
                        placement="bottomLeft"
                    >
                        <Tag
                            color={statusColors[status]}
                            style={{ cursor: "pointer" }}
                        >
                            {status} <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 4, fontSize: 10 }} />
                        </Tag>
                    </Dropdown>
                );
            },
        },
        {
            title: "Hạn sử dụng",
            dataIndex: "end_at",
            key: "end_at",
            width: 150,
            render: (timestamp) => formatDate(timestamp),
        },
        {
            title: "Đã lấy",
            key: "taken_info",
            width: 180,
            render: (_, record) => {
                if (record.taken_by_user_id && record.taken_at) {
                    const takenByUser = record.takenByUser;
                    const userName = takenByUser
                        ? (takenByUser.name || takenByUser.email || `User #${takenByUser.userId}`)
                        : `User #${record.taken_by_user_id}`;
                    return (
                        <div>
                            <div style={{ fontWeight: 500 }}>{userName}</div>
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                                {formatDate(record.taken_at)}
                            </div>
                        </div>
                    );
                }
                return <span style={{ color: "#d9d9d9" }}>-</span>;
            },
        },
        {
            title: "Đã dùng",
            key: "used_at",
            width: 140,
            render: (_, record) => {
                const usedAt = record.userVoucher?.used_at;
                if (usedAt) {
                    return (
                        <div style={{ fontSize: 12, color: "#52c41a" }}>
                            {formatDate(usedAt)}
                        </div>
                    );
                }
                return <span style={{ color: "#d9d9d9" }}>-</span>;
            },
        },
        {
            title: "Hành động",
            key: "action",
            width: 200,
            fixed: "right",
            render: (_, record) => (
                <Space size="middle">
                    {record.status === "PENDING" && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<FontAwesomeIcon icon={faCheck} />}
                            onClick={() => handleApprove(record.id)}
                        >
                            Duyệt
                        </Button>
                    )}
                    <Button
                        size="small"
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => navigate(`/voucher/${record.id}/edit`)}
                    >
                        Sửa
                    </Button>
                    <Button
                        size="small"
                        icon={<FontAwesomeIcon icon={faCopy} />}
                        onClick={() => handleCopy(record)}
                        title="Sao chép voucher"
                    >
                        Sao chép
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6 max-w-full overflow-x-hidden">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4 min-w-0">
                <h1 className="text-2xl font-bold shrink-0">Quản lý Voucher</h1>
                <Space>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => navigate("/voucher/create")}
                    >
                        Tạo mới
                    </Button>
                    <Button
                        icon={<FontAwesomeIcon icon={faFileImport} />}
                        onClick={() => navigate("/voucher/import")}
                    >
                        Import CSV
                    </Button>
                    <Button
                        icon={<FontAwesomeIcon icon={faChartBar} />}
                        onClick={() => navigate("/voucher/stats")}
                    >
                        Thống kê
                    </Button>
                    <Button
                        icon={<FontAwesomeIcon icon={faFileInvoiceDollar} />}
                        onClick={() => setTaxReportOpen(true)}
                    >
                        Báo cáo thuế
                    </Button>
                </Space>
            </div>

            {/* Filters */}
            <div className="mb-4 flex gap-2 flex-wrap">
                <Input
                    placeholder="Tìm kiếm theo mã voucher"
                    className="w-48"
                    value={filters.code}
                    onChange={(e) =>
                        setFilters({ ...filters, code: e.target.value })
                    }
                    allowClear
                />
                <select
                    className="px-3 py-2 border rounded"
                    value={filters.status}
                    onChange={(e) =>
                        setFilters({ ...filters, status: e.target.value })
                    }
                >
                    <option value="">Tất cả trạng thái</option>
                    <option value="PENDING">PENDING</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="TAKEN">TAKEN</option>
                    <option value="OUT_OF_STOCK">OUT_OF_STOCK</option>
                    <option value="EXPIRED">EXPIRED</option>
                </select>
                <select
                    className="px-3 py-2 border rounded"
                    value={filters.partner}
                    onChange={(e) =>
                        setFilters({ ...filters, partner: e.target.value })
                    }
                >
                    <option value="">Tất cả đối tác</option>
                    <option value="SHOPEE">Shopee</option>
                    <option value="LAZADA">Lazada</option>
                    <option value="TIKTOK">TikTok</option>
                    <option value="OTHER">Khác</option>
                </select>
                <Input
                    type="number"
                    placeholder="Lọc theo mệnh giá"
                    className="w-40"
                    value={filters.value}
                    onChange={(e) =>
                        setFilters({ ...filters, value: e.target.value })
                    }
                    allowClear
                />
                <DatePicker.RangePicker
                    placeholder={["Từ ngày", "Đến ngày"]}
                    format="DD/MM/YYYY"
                    className="w-64"
                    presets={DATE_PRESETS}
                    value={
                        filters.start_date && filters.end_date
                            ? [
                                  dayjs(filters.start_date, "YYYY-MM-DD"),
                                  dayjs(filters.end_date, "YYYY-MM-DD"),
                              ]
                            : null
                    }
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                            setFilters({
                                ...filters,
                                start_date: dates[0].format("YYYY-MM-DD"),
                                end_date: dates[1].format("YYYY-MM-DD"),
                            });
                        } else {
                            setFilters({
                                ...filters,
                                start_date: "",
                                end_date: "",
                            });
                        }
                    }}
                />
                <Button
                    onClick={() => {
                        setFilters({
                            status: "",
                            partner: "",
                            value: "",
                            code: "",
                            start_date: "",
                            end_date: "",
                        });
                        setPagination({ ...pagination, current: 1 });
                    }}
                >
                    Xóa bộ lọc
                </Button>
            </div>

            <VoucherSummaryBar stats={voucherStats} />
            <MonthlyTaxReportModal
                open={taxReportOpen}
                onClose={() => setTaxReportOpen(false)}
                title="Báo cáo đổi voucher theo tháng (khai thuế)"
                filePrefix="voucher"
                labels={VOUCHER_TAX_LABELS}
                note="Lượt 0đ / admin tặng không trừ tiền nên không tính vào số user và tổng tiền. Voucher bị huỷ (đã hoàn tiền) không tính."
                fetchSummary={voucherApi.getRedemptionSummary}
                exportCsv={voucherApi.exportRedemptions}
            />

            {/* Bulk action bar */}
            {selectedRowKeys.length > 0 && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded flex items-center gap-3">
                    <span className="font-medium text-blue-700">Đã chọn {selectedRowKeys.length} voucher</span>
                    <Button
                        type="primary"
                        size="small"
                        icon={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => setBulkModalVisible(true)}
                    >
                        Sửa giá đổi hàng loạt
                    </Button>
                    <Button
                        size="small"
                        type="primary"
                        style={{ background: "#10b981" }}
                        icon={<FontAwesomeIcon icon={faGift} />}
                        onClick={() => setAssignModalVisible(true)}
                    >
                        Tặng Voucher cho User
                    </Button>
                    <Button size="small" onClick={() => setSelectedRowKeys([])}>Bỏ chọn</Button>
                </div>
            )}

            <Table
                columns={columns}
                dataSource={vouchers}
                loading={loading}
                rowKey="id"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                    preserveSelectedRowKeys: true,
                }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Tổng: ${total} voucher`,
                    onChange: (page, pageSize) => {
                        setPagination({ ...pagination, current: page, pageSize });
                    },
                }}
                scroll={{ x: "max-content" }}
            />
            {/* Bulk edit redeem cost modal */}
            <Modal
                title={`Sửa giá đổi hàng loạt (${selectedRowKeys.length} voucher)`}
                open={bulkModalVisible}
                onOk={handleBulkUpdateRedeemCost}
                onCancel={() => { setBulkModalVisible(false); setBulkRedeemCost(null); }}
                confirmLoading={bulkLoading}
                okText="Cập nhật"
                cancelText="Hủy"
            >
                <div className="mb-2 text-content-2">Nhập giá đổi mới (điểm) áp dụng cho tất cả voucher đã chọn:</div>
                <InputNumber
                    className="w-full"
                    min={0}
                    value={bulkRedeemCost}
                    onChange={setBulkRedeemCost}
                    placeholder="Nhập giá đổi (điểm) — 0 = miễn phí"
                    formatter={(v) => v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""}
                    parser={(v) => v.replace(/,/g, "")}
                />
            </Modal>

            {/* Bulk assign voucher to users modal */}
            <Modal
                title={assignResult
                    ? `Kết quả tặng Voucher — ✅ ${assignResult.assignedCount} thành công, ❌ ${assignResult.skippedCount} thất bại`
                    : `Tặng Voucher cho User (${selectedRowKeys.length} voucher đã chọn)`
                }
                open={assignModalVisible}
                onOk={assignResult ? null : handleBulkAssign}
                onCancel={() => { setAssignModalVisible(false); setAssignUserIds(""); setAssignResult(null); }}
                confirmLoading={assignLoading}
                okText={assignResult ? null : "Tặng Voucher"}
                cancelText="Đóng"
                footer={assignResult ? [
                    <Button key="close" onClick={() => { setAssignModalVisible(false); setAssignUserIds(""); setAssignResult(null); }}>
                        Đóng
                    </Button>
                ] : undefined}
                width={assignResult ? 640 : 520}
            >
                {assignResult ? (
                    // Result view
                    <div>
                        <Table
                            size="small"
                            pagination={false}
                            scroll={{ y: 320 }}
                            dataSource={[
                                ...assignResult.assigned.map((r) => ({ ...r, _status: "success", key: `ok-${r.userId}` })),
                                ...assignResult.skipped.map((r) => ({ ...r, _status: "fail", key: `skip-${r.userId}` })),
                            ]}
                            columns={[
                                {
                                    title: "User ID",
                                    dataIndex: "userId",
                                    width: 100,
                                },
                                {
                                    title: "Voucher ID",
                                    dataIndex: "voucherId",
                                    width: 100,
                                    render: (v) => v ?? "-",
                                },
                                {
                                    title: "Kết quả",
                                    key: "result",
                                    render: (_, r) =>
                                        r._status === "success"
                                            ? <Tag color="green">✅ Thành công (value: {r.value?.toLocaleString("vi-VN")}đ)</Tag>
                                            : <Tag color="red">❌ {r.reason}</Tag>,
                                },
                            ]}
                        />
                    </div>
                ) : (
                    // Input view
                    <>
                        <div className="mb-2 text-content-2">
                            Voucher ACTIVE sẽ được gán trực tiếp vào ví user (không trừ tiền).
                            Mỗi voucher gán cho 1 user theo thứ tự.
                        </div>
                        <div className="mb-2 text-sm text-orange-600">
                            {(() => {
                                const activeCount = selectedRowKeys.filter((id) => {
                                    const v = vouchers.find((voucher) => voucher.id === id);
                                    return v && v.status === "ACTIVE";
                                }).length;
                                return `${activeCount}/${selectedRowKeys.length} voucher đang ở trạng thái ACTIVE`;
                            })()}
                        </div>
                        <Input.TextArea
                            rows={5}
                            value={assignUserIds}
                            onChange={(e) => setAssignUserIds(e.target.value)}
                            placeholder="Nhập User ID cách nhau bởi dấu phẩy hoặc xuống dòng&#10;VD: 123, 456, 789"
                        />
                        <div className="mt-1 text-sm text-content-3">
                            {assignUserIds.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean)
                                .filter((s) => Number.isInteger(Number(s)) && Number(s) > 0).length} user hợp lệ
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}

export default VoucherList;

